import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  AUTH_CLEARED_EVENT,
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getStoredSession,
  setStoredSession,
  setTokens,
} from "@/shared/infrastructure/auth/token-storage";
import type { AuthSession } from "../../domain/entities";
import type {
  AuthResult,
  InitiateRegistrationInput,
  LoginCredentials,
  OnboardingInput,
} from "../../application/ports/auth.repository.port";
import {
  useCancelAccountDeletionWithPasswordMutation,
  useDoctorLoginMutation,
  useForgotPasswordMutation,
  useGoogleLoginMutation,
  useInitiateRegistrationMutation,
  useLoginMutation,
  useLogoutMutation,
  useOnboardingMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
  useVerifyEmailMutation,
} from "../queries/auth.queries";
import { usersHttpRepository } from "@/modules/users/infrastructure/http/users.http-repository";
import { isTermsAcceptanceRequiredError } from "@/shared/domain/api-error";
import { extractRoleFromToken } from "../auth-token-utils";
import { doctorsHttpRepository } from "@/modules/doctors/infrastructure/http/doctors.http-repository";
import { PUBLIC_ROUTES } from "../public-routes";

interface AuthContextValue {
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle?: (idToken: string) => Promise<void>;
  /** Cancela a exclusão agendada com e-mail e senha e entra (mesmo fluxo do login). */
  cancelDeletionAndLogin?: (credentials: LoginCredentials) => Promise<void>;
  doctorLogin: (credentials: LoginCredentials) => Promise<void>;
  onboarding: (input: OnboardingInput) => Promise<void>;
  logout: () => Promise<void>;
  initiateRegistration: (input: InitiateRegistrationInput) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  setPassword: (password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  updateSession?: (patch: Partial<AuthSession>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredSession(): AuthSession | null {
  return getStoredSession() as AuthSession | null;
}

function persistSession(session: AuthSession) {
  setStoredSession(session);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loginMutation = useLoginMutation();
  const googleLoginMutation = useGoogleLoginMutation();
  const cancelDeletionMutation = useCancelAccountDeletionWithPasswordMutation();
  const doctorLoginMutation = useDoctorLoginMutation();
  const onboardingMutation = useOnboardingMutation();
  const logoutMutation = useLogoutMutation();
  const initiateRegistrationMutation = useInitiateRegistrationMutation();
  const verifyEmailMutation = useVerifyEmailMutation();
  const setPasswordMutation = useSetPasswordMutation();
  const forgotPasswordMutation = useForgotPasswordMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  useEffect(() => {
    const handleAuthCleared = () => {
      setSession(null);
      if (typeof window !== "undefined" && import.meta.env?.MODE !== "test") {
        const path = window.location.pathname;
        const isAuthDoctorRoute = path.startsWith("/doctor") && !path.startsWith("/doctor-login");


        const isPublicRoute = PUBLIC_ROUTES.some((pub) => path === pub || (pub !== "/" && path.startsWith(pub)));

        if (isAuthDoctorRoute) {
          window.location.href = "/doctor-login";
        } else if (!isPublicRoute) {
          window.location.href = "/login";
        }
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
      return () => window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    }
  }, []);

  useEffect(() => {
    if (getAccessToken()) {
      const restored = readStoredSession();
      if (restored) {
        setSession(restored);
      } else {
        clearAuthStorage();
      }
    }
    setIsLoading(false);
  }, []);

  // Guarda os tokens e monta a sessão do paciente a partir do perfil; sem perfil válido, desfaz tudo.
  const startPatientSession = async (result: AuthResult) => {
    setTokens(result);
    try {
      // As respostas de login não trazem o role real (sempre "patient" no repositório) — busca o
      // perfil para refletir corretamente contas admin, já que o guard de UI depende disso.
      const profile = await usersHttpRepository.getById(result.userId);
      const nextSession: AuthSession = {
        userId: result.userId,
        fullName: profile.fullName || result.fullName,
        email: result.email,
        role: profile.role,
        profilePicture: profile.profilePicture ?? null,
      };
      persistSession(nextSession);
      setSession(nextSession);
    } catch (error) {
      if (isTermsAcceptanceRequiredError(error)) {
        const nextSession: AuthSession = {
          userId: result.userId,
          fullName: result.fullName,
          email: result.email,
          role: extractRoleFromToken(result.accessToken) ?? "patient",
        };
        persistSession(nextSession);
        setSession(nextSession);
        return;
      }
      clearAuthStorage();
      throw error;
    }
  };

  const loginWithGoogle = async (idToken: string) => {
    await startPatientSession(await googleLoginMutation.mutateAsync(idToken));
  };

  const login = async (credentials: LoginCredentials) => {
    await startPatientSession(await loginMutation.mutateAsync(credentials));
  };

  const cancelDeletionAndLogin = async (credentials: LoginCredentials) => {
    await startPatientSession(await cancelDeletionMutation.mutateAsync(credentials));
  };

  const doctorLogin = async (credentials: LoginCredentials) => {
    const result = await doctorLoginMutation.mutateAsync(credentials);
    setTokens(result);
    let profilePicture: string | null = null;
    let fullName = result.fullName;
    try {
      const doctor = await doctorsHttpRepository.getById(result.userId);
      profilePicture = doctor.profilePicture ?? null;
      fullName = doctor.name || result.fullName;
    } catch {
      // ignore
    }
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName,
      email: result.email,
      role: result.role,
      profilePicture,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const onboarding = async (input: OnboardingInput) => {
    const result = await onboardingMutation.mutateAsync(input);
    setTokens(result);
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName: result.fullName,
      email: result.email,
      role: result.role,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const initiateRegistration = async (input: InitiateRegistrationInput) => {
    await initiateRegistrationMutation.mutateAsync(input);
  };

  // Autentica com um par de tokens de sessão curta — permite chamar /auth/set-password
  // (protegido) na etapa seguinte, mas ainda não é uma conta com senha definida.
  const verifyEmail = async (token: string) => {
    const result = await verifyEmailMutation.mutateAsync(token);
    setTokens(result);
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName: result.fullName,
      email: result.email,
      role: result.role,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const setPassword = async (password: string) => {
    await setPasswordMutation.mutateAsync(password);
  };

  const forgotPassword = async (email: string) => {
    await forgotPasswordMutation.mutateAsync(email);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    await resetPasswordMutation.mutateAsync({ token, newPassword });
  };

  const logout = async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) await logoutMutation.mutateAsync(refreshToken);
    } finally {
      clearAuthStorage();
      setSession(null);
      window.location.href = import.meta.env.VITE_INSTITUTIONAL_BASE_URL || "/";
    }
  };

  const updateSession = (patch: Partial<AuthSession>) => {
    setSession((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...patch };
      persistSession(updated);
      return updated;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        isAuthenticated: !!session,
        isLoading,
        login,
        loginWithGoogle,
        cancelDeletionAndLogin,
        doctorLogin,
        onboarding,
        logout,
        initiateRegistration,
        verifyEmail,
        setPassword,
        forgotPassword,
        resetPassword,
        updateSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
}
